import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Toast, { ToastType } from '../atoms/Toast';
import HelpTechIcon from '../icons/HelpTechIcon';
import HelpProfileIcon from '../icons/HelpProfileIcon';
import HelpAdditionsIcon from '../icons/HelpAdditionsIcon';
import HelpAccountsIcon from '../icons/HelpAccountsIcon';
import HelpTrainingIcon from '../icons/HelpTrainingIcon';
import HelpDescriptionsIcon from '../icons/HelpDescriptionsIcon';
import HelpDocumentationIcon from '../icons/HelpDocumentationIcon';
import HelpVideoIcon from '../icons/HelpVideoIcon';
import HelpFootballIcon from '../icons/HelpFootballIcon';
import TechHelpModal from '../molecules/TechHelpModal';
import LoginSubscriptionModal from '../molecules/LoginSubscriptionModal';
import PlayerAdditionChangeModal from '../molecules/PlayerAdditionChangeModal';
import AddRemoveAccountModal from '../molecules/AddRemoveAccountModal';
import ScheduleTrainingModal from '../molecules/ScheduleTrainingModal';
import OtherHelpModal from '../molecules/OtherHelpModal';
import ScoreDescriptionsModal from '../molecules/ScoreDescriptionsModal';
import ApiDocumentationModal from '../molecules/ApiDocumentationModal';
import { State } from '../redux/reducers';
import User from '../types/User';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import {
  COLOR_TEXT,
  COLOR_BORDER,
  COLOR_SHADOW,
  COLOR_WHITE,
  COLOR_BACKGROUND_LIGHT,
  COLOR_ORANGE,
} from '../styles/colors';
import MEDIA from '../styles/media';

enum HelpTopicType {
  TECH_HELP = 'tech-help',
  LOGIN_SUBSCRIPTION = 'login-subscription',
  ADD_REMOVE_ACCOUNTS = 'add-remove-accounts',
  PLAYER_ADDITIONS_CHANGES = 'player-additions-changes',
  SCHEDULE_TRAINING = 'schedule-training',
  SCORE_DESCRIPTION = 'score-descriptions',
  API_DOCUMENTATION = 'api-documentation',
  OTHER = 'other',
}

interface HelpTopic {
  type: HelpTopicType;
  text: string;
  icon: any;
  iconSize?: number;
  modal: any;
}

const HELP_TOPICS:HelpTopic[] = [
  {
    type: HelpTopicType.TECH_HELP,
    text: 'Tech Help',
    icon: HelpTechIcon,
    modal: TechHelpModal
  },
  {
    type: HelpTopicType.LOGIN_SUBSCRIPTION,
    text: 'Login / Subscription',
    icon: HelpProfileIcon,
    modal: LoginSubscriptionModal,
  },
  {
    type: HelpTopicType.PLAYER_ADDITIONS_CHANGES,
    text: 'Player Additions / Changes',
    icon: HelpAdditionsIcon,
    modal: PlayerAdditionChangeModal,
  },
  {
    type: HelpTopicType.ADD_REMOVE_ACCOUNTS,
    text: 'Add / Remove Accounts',
    icon: HelpAccountsIcon,
    modal: AddRemoveAccountModal,
  },
  {
    type: HelpTopicType.SCHEDULE_TRAINING,
    text: 'Schedule Training',
    icon: HelpTrainingIcon,
    modal: ScheduleTrainingModal,
  },
  {
    type: HelpTopicType.SCORE_DESCRIPTION,
    text: 'Score Descriptions',
    icon: HelpDescriptionsIcon,
    modal: ScoreDescriptionsModal,
  },
  {
    type: HelpTopicType.API_DOCUMENTATION,
    text: 'API Documentation',
    icon: HelpDocumentationIcon,
    modal: ApiDocumentationModal,
  },
  {
    type: HelpTopicType.OTHER,
    text: 'Other',
    icon: HelpFootballIcon,
    modal: OtherHelpModal,
  }
];

interface HelpPageCardsProps {
  user?: User;
}

const useStyles = makeStyles(theme => ({
  container: {
    overflow: 'auto',
    padding: theme.spacing(2),
    width: '100%',

    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(5),
    },

    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(10),
    }
  },

  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    alignItems: 'start',
    gap: theme.spacing(1),

    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing(2),
    },

    '@media (min-width: 1281px)': {
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: theme.spacing(3),
    }
  },

  button: {
    boxSizing: 'border-box',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 0,
    backgroundColor: `${COLOR_WHITE}`,
    border: `4px solid ${COLOR_BORDER}`,
    borderRadius: '20px',
    transition: 'border-color 0.3s',

    '&:hover': {
      backgroundColor: 'inherit',
      borderColor: COLOR_ORANGE,
      color: 'inherit',
    },

    [theme.breakpoints.up('sm')]: {
      aspectRatio: '1 / 1.2',
      borderRadius: '40px',
    },

    '@media (min-width: 1801px)': {
      aspectRatio: '1 / 1.5',
    },
  },

  buttonTitle: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(2),
    minHeight: '90px',
    textAlign: 'left',
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.1,
    fontWeight: 700,
    color: '#3A3A3A',

    [theme.breakpoints.up('sm')]: {
      minHeight: 0,
      flexBasis: '35%',
      padding: theme.spacing(3, 4),
      fontSize: theme.typography.pxToRem(20),
    },

    '@media (min-width: 1801px)': {
      justifyContent: 'flex-start',
      padding: theme.spacing(4, 5, 3),
      fontSize: theme.typography.pxToRem(28),
    },
  },

  buttonIcon: {
    boxSizing: 'border-box',
    flexGrow: 1,
    minHeight: '90px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    backgroundColor: COLOR_BACKGROUND_LIGHT,

    [theme.breakpoints.up('sm')]: {
      minHeight: 0,
    },
  },

  icon: {    
    '& svg': {
      display: 'block',
      maxWidth: '60px',
      maxHeight: '60px',
    },

    [theme.breakpoints.up('sm')]: {
      '& svg': {
        maxHeight: '100px',
      },
    },

    '@media (min-width: 1801px)': {
      '& svg': {
        maxWidth: '120px',
        maxHeight: '120px',
      },
    },
  },

  heading: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: theme.spacing(0,0,2),
    fontSize: theme.typography.pxToRem(16),
  },

  dialog : {
    overflow: 'auto',
  },

  dropDown: {
    marginBottom: theme.spacing(5),
    padding: 0,
  },

  dropDownSelect: {
    height: '50px',
  },

  input: {
    padding: 0,
    marginBottom: theme.spacing(2),
  },

  inputHalf: {
    width: '100%',
    boxSizing: 'border-box',
    padding: 0,
    margin: theme.spacing(0, 0, 2, 1),

    '&:nth-of-type(1)': {
      marginLeft: 0,
    }
  },

  inputField: {
    padding: theme.spacing(.5),
  },

  name: {
    display: 'flex',
  },

  multiSelectClassName: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    minHeight: '50px',
    fontSize: theme.typography.pxToRem(16),
  },

  contentClassName: {
    position: 'relative'
  },
}), { name: HelpPageCards.name });

function HelpPageCards (props:HelpPageCardsProps) {
  const classes = useStyles();
  const { user } = props;

  const history = useHistory();

  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [selectedTopicType, setSelectedTopicType] = useState<string>('');
  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [toastType, setToastType] = useState<ToastType>(ToastType.SUCCESS);
  const [toastMessage, setToastMessage] = useState<string>('');

  useEffect(() => {
    if (user && Object.keys(user).length) {
      if (!user.accessLevel.canAccessHelp) {
        history.push(`/dashboard?no-access=${history.location.pathname}`)
      }
    }
  }, [user]);

  function handleClick (topicType:string) {
    setIsPopupVisible(true);
    setSelectedTopicType(topicType);
  }

  function showToast (message:string, type:ToastType = ToastType.SUCCESS) {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }

  const selectedTopic = HELP_TOPICS.find((topic:HelpTopic) => topic.type === selectedTopicType);
  const Modal = selectedTopic && selectedTopic.modal;

  return (
    <div className={classes.container}>
      <div className={classes.cardsContainer}>
        {HELP_TOPICS.map((topic:HelpTopic, index:number) => (
          <button
            key={index}
            className={classes.button}
            onClick={() => handleClick(topic.type)}
          >
            <div className={classes.buttonTitle}>
              {topic.text}
            </div>

            <div className={classes.buttonIcon}>
              <topic.icon
                className={classes.icon}
                style={topic.iconSize ? { width: topic.iconSize, height: topic.iconSize } : undefined}
              />
            </div>
          </button>
        ))}
      </div>

      {(isPopupVisible && Modal) && (
        <Modal
          isPopupVisible={isPopupVisible}
          onClose={() => {setIsPopupVisible(false)}}
          className={classes.dialog}
          headingClassName={classes.heading}
          inputFieldClassName={classes.inputField}
          inputClassName={classes.input}
          inputHalfClassName={classes.inputHalf}
          dropDownClassName={classes.dropDown}
          dropDownSelectClassName={classes.dropDownSelect}
          fullNameClassName={classes.name}
          multiSelectClassName={classes.multiSelectClassName}
          contentClassName={classes.contentClassName}
          onError={() => {
            showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
          }}
          onSuccess={() => {
            showToast('Submitted', ToastType.SUCCESS);
          }}
        />
      )}

      <Toast
        visible={toastVisible}
        type={toastType}
        onHide={() => setToastVisible(false)}
      >
        {toastMessage}
      </Toast>
    </div>
  );
}

const mapStateToProps = (state:State) => {
  return {
    user: state.user,
  };
};

export default connect(
  mapStateToProps,
)(HelpPageCards);
